<template>
  <div class="global-customers">
    <div class="section-header">
      <div class="section-header__left">
        <div class="section-header__icon">
          <i class="el-icon-office-building"></i>
        </div>
        <div>
          <h2 class="section-header__title">{{ $t('settings.globalCustomers') }}</h2>
          <p class="section-header__subtitle">{{ pagination.total }} {{ $t('settings.entries') }}</p>
        </div>
      </div>

      <div class="section-header__actions">
        <base-button
          icon="el-icon-back"
          @click="$router.push({path: '/app/global'})">
          {{ $t('settings.backToDashboard') }}
        </base-button>
        <base-button
          type="primary"
          icon="el-icon-refresh"
          :loading="loading"
          @click="loadCustomers">
          {{ $t('settings.refresh') }}
        </base-button>
      </div>
    </div>

    <div class="search-container">
      <el-input
        class="table-search"
        v-model="searchTerm"
        :placeholder="$t('settings.customer.search')"
        prefix-icon="el-icon-search"
        clearable
        @clear="clearSearch"
        @input="debouncedSearch">
      </el-input>
      <div class="search-hint">{{ $t('settings.searchHint') }}</div>
    </div>

    <div class="table-card">
      <base-table
        :data="customers"
        :loading="loading"
        :empty-text="$t('settings.customersTableEmpty')"
        highlight-current-row
        class="connections-table"
        style="width: 100%"
        @current-change="onRowSelected">

        <el-table-column prop="name" min-width="160" :label="$t('settings.name')">
          <template slot-scope="scope">
            <span class="cell-bold">{{ scope.row.name }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="appLoginDomain" min-width="180" :label="$t('settings.appLoginDomain')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.appLoginDomain">
              <i class="el-icon-link cell-url__icon"></i>
              <span>{{ scope.row.appLoginDomain }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="mail" min-width="200" :label="$t('settings.mail')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.mail">
              <i class="el-icon-message cell-url__icon"></i>
              <span>{{ scope.row.mail }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column min-width="150" :label="$t('settings.tenantName')">
          <template slot-scope="scope">
            <el-tag v-if="tenantName(scope.row)" size="mini" type="info" class="domain-tag">
              {{ tenantName(scope.row) }}
            </el-tag>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column min-width="190" :label="$t('settings.lastSynchronized')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.lastSynchronized">
              <i class="el-icon-time cell-url__icon"></i>
              <span>{{ scope.row.lastSynchronized | moment($t('settings.dateTimeFormat')) }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column min-width="190" :label="$t('settings.lastSynchronizedTry')">
          <template slot-scope="scope">
            <div
              v-if="scope.row.lastSynchronizeTry"
              class="sync-status"
              :class="wasLastSynchronizeTrySuccessfully(scope.row) ? 'sync-status--ok' : 'sync-status--err'">
              <i
                :class="wasLastSynchronizeTrySuccessfully(scope.row) ? 'el-icon-circle-check' : 'el-icon-circle-close'"></i>
              {{ scope.row.lastSynchronizeTry | moment($t('settings.dateTimeFormat')) }}
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column min-width="190" :label="$t('settings.nextSynchronizeTry')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.nextSynchronizeTry">
              <i class="el-icon-alarm-clock cell-url__icon"></i>
              <span>{{ scope.row.nextSynchronizeTry | moment($t('settings.dateTimeFormat')) }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="userCount" width="130" align="center" :label="$t('settings.userCount')">
          <template slot-scope="scope">
            <el-tag type="info" size="mini" class="domain-tag">
              <i class="el-icon-user"></i> {{ scope.row.userCount || 0 }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="localUserCount" width="110" align="center" :label="$t('settings.localUserCount')">
          <template slot-scope="scope">
            <el-tag size="mini" class="domain-tag">{{ scope.row.localUserCount || 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="usersWithTokenCount" width="120" align="center"
                         :label="$t('settings.usersWithTokenCount')">
          <template slot-scope="scope">
            <el-tag type="success" size="mini" class="domain-tag">
              <i class="el-icon-mobile-phone"></i> {{ scope.row.usersWithTokenCount || 0 }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="default" width="130" align="center" :label="$t('settings.defaultCustomer')">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.default"
              @click.native.stop
              @change="updateDefaultStatus(scope.row)">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column align="center" fixed="right" width="180" :label="$t('settings.operations')">
          <template slot-scope="customer">
            <div class="action-cell">
              <el-tooltip :content="$t('settings.edit')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-edit"
                  size="mini"
                  circle
                  @click.stop.prevent="viewCustomer(customer.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.showCustomerAccounts')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-s-custom"
                  size="mini"
                  circle
                  @click.stop.prevent="viewCustomerAccounts(customer.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.syncCustomerAccounts')" placement="top">
                <el-button
                  :disabled="!canSync(customer.row)"
                  type="primary"
                  icon="el-icon-refresh"
                  size="mini"
                  circle
                  :loading="customer.row.loading"
                  @click.stop.prevent="syncCustomerAccounts(customer.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.deleteCustomer')" placement="top">
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="mini"
                  circle
                  @click.stop.prevent="onCustomerDelete(customer.row)">
                </el-button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </base-table>

      <el-pagination
        v-if="pagination.total > 0"
        class="customers-pagination"
        @size-change="handlePageSizeChange"
        @current-change="handlePageChange"
        :current-page.sync="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total">
      </el-pagination>
    </div>

    <base-delete-dialog
      :visible.sync="deleteCustomerDialog"
      :title="$t('settings.deleteCustomer')"
      :text="$t('settings.deleteCustomerInfoText')"
      :confirm-label="$t('settings.deleteCustomer')"
      :loading="loading"
      @confirm="confirmDeleteCustomer">
    </base-delete-dialog>
  </div>
</template>

<script>
import BaseButton from '@/components/common/BaseButton';
import {adminCustomerService} from '@/services/AdminCustomerService.class';
import {customerService} from '@/services/CustomerService.class';
import BaseTable from '@/components/common/BaseTable';
import BaseDeleteDialog from '@/components/common/BaseDeleteDialog';
import moment from 'moment';

export default {
  name: "GlobalCustomers",
  components: {BaseTable, BaseDeleteDialog, BaseButton},
  data() {
    return {
      customers: [],
      searchTerm: '',
      loading: false,
      deleteCustomerDialog: false,
      selectedCustomer: null,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      searchTimeout: null
    };
  },
  mounted() {
    this.loadCustomers();
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    extractPage(data) {
      if (Array.isArray(data)) {
        return {rows: data, total: data.length};
      }
      if (data && Array.isArray(data.content)) {
        return {
          rows: data.content,
          total: typeof data.totalElements === 'number' ? data.totalElements : data.content.length
        };
      }
      return {rows: [], total: 0};
    },
    mapCustomer(raw) {
      return {
        ...raw,
        userCount: raw.userCount !== undefined ? raw.userCount : raw.remoteUserCount,
        remoteAccountProviderCount: raw.remoteAccountProviderCount !== undefined
          ? raw.remoteAccountProviderCount
          : (Array.isArray(raw.adConnections) ? raw.adConnections.length : 0),
        default: !!raw.default,
        loading: false
      };
    },

    loadCustomers() {
      this.loading = true;

      const filterParams = {
        search: this.searchTerm || undefined
      };
      const pageParams = {
        page: this.pagination.page - 1,
        pageSize: this.pagination.pageSize
      };

      adminCustomerService.getAllCustomers(filterParams, pageParams)
        .then(response => {
          const {rows, total} = this.extractPage(response.data);
          this.customers = rows.map(this.mapCustomer);
          this.pagination.total = total;
        })
        .catch(() => {
          this.customers = [];
          this.pagination.total = 0;
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    tenantName(customer) {
      if (customer.tenant && customer.tenant.name) {
        return customer.tenant.name;
      }
      return customer.tenantName || '';
    },

    tenantId(customer) {
      if (customer.tenant && customer.tenant.id) {
        return customer.tenant.id;
      }
      return customer.tenantId || null;
    },

    canSync(customer) {
      return !!customer.remoteAccountProviderCount && customer.remoteAccountProviderCount > 0;
    },

    handlePageSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.loadCustomers();
    },
    handlePageChange() {
      this.loadCustomers();
    },
    clearSearch() {
      this.searchTerm = '';
      this.pagination.page = 1;
      this.loadCustomers();
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadCustomers();
      }, 300);
    },

    updateDefaultStatus(customer) {
      customerService.patch(customer)
        .then(() => {
          this.$message.success(this.$t('settings.patchSuccess'));
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
          customer.default = !customer.default;
        });
    },

    onRowSelected(customer) {
      if (customer && customer.id) {
        this.viewCustomerAccounts(customer);
      }
    },
    viewCustomer(customer) {
      if (customer.id) {
        this.$router.push({path: `/app/settings/${customer.id}/edit`});
      }
    },
    viewCustomerAccounts(customer) {
      if (customer.id) {
        this.$router.push({path: `/app/settings/${customer.id}/accounts`});
      }
    },

    syncCustomerAccounts(customer) {
      customer.loading = true;
      customerService.syncAccounts(customer.id)
        .then(() => {
          this.$message({
            message: `${customer.name} ${this.$t('settings.showCustomerAccountsSuccess')}`,
            type: 'success'
          });
        })
        .catch(() => {
          this.$message({
            message: `${customer.name} ${this.$t('settings.showCustomerAccountsError')}`,
            type: 'error'
          });
        })
        .finally(() => {
          customer.loading = false;
          this.loadCustomers();
        });
    },

    onCustomerDelete(customer) {
      this.selectedCustomer = customer;
      this.deleteCustomerDialog = true;
    },
    confirmDeleteCustomer() {
      if (!this.selectedCustomer) {
        return;
      }
      this.loading = true;

      const tenantId = this.tenantId(this.selectedCustomer);
      const request = tenantId
        ? adminCustomerService.deleteCustomerFromTenant(tenantId, this.selectedCustomer.id)
        : customerService.delete(this.selectedCustomer.id);

      request
        .then(() => {
          this.$message({type: 'success', message: this.$t('settings.customerDeleted')});
          this.deleteCustomerDialog = false;
          this.selectedCustomer = null;
          this.loadCustomers();
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    wasLastSynchronizeTrySuccessfully(customer) {
      if (!customer.lastSynchronized) {
        return false;
      }
      return moment(customer.lastSynchronized) >= moment(customer.lastSynchronizeTry);
    }
  }
}
</script>

<style lang="scss">
.global-customers .connections-table .el-table__body-wrapper tr {
  cursor: pointer;

  &.current-row td {
    background: var(--bg-hover) !important;
  }
}
</style>

<style scoped lang="scss">
.global-customers {
  padding: 0 15px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 18px;
    }
  }

  &__title {
    margin: 0 0 2px;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.search-container {
  margin-bottom: 16px;

  .table-search {
    max-width: 400px;
    margin-bottom: 5px;
  }

  .search-hint {
    font-size: 12px;
    color: var(--text-secondary);
    margin-left: 5px;
  }
}

.table-card {
  background: var(--bg-surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.customers-pagination {
  padding: 14px 0;
  text-align: center;
  border-top: 1px solid var(--border);
}

.cell-bold {
  font-weight: 600;
  font-size: 13.5px;
  color: var(--text-primary);
}

.cell-url {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 13px;

  &__icon {
    color: var(--text-muted);
    font-size: 14px;
    flex-shrink: 0;
  }
}

.cell-empty {
  color: var(--text-muted);
}

.sync-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;

  &--ok {
    color: var(--success);
  }

  &--err {
    color: var(--danger);
  }

  i {
    font-size: 15px;
  }
}

.domain-tag {
  font-weight: 500;
  border-radius: 6px;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

@media (max-width: 767px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
}
</style>
